import { useTranslations } from "use-intl";
import { useState, useTransition } from "react";
import { useNavigate } from "react-router";
import { CheckCircle2, Copy, MoreVertical, Share2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { deletePlan, duplicatePlan, setActivePlan } from "@/lib/actions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SharePlanDrawer } from "./share-plan";

export function PlanActions({ planId, planName, active }: { planId: string; planName: string; active: boolean }) {
  const t = useTranslations("plans");
  const tc = useTranslations("common");
  const [confirm, setConfirm] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [pending, start] = useTransition();
  const navigate = useNavigate();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-lg" className="rounded-full" aria-label={tc("edit")}>
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {!active && (
            <DropdownMenuItem
              onSelect={() =>
                start(async () => {
                  await setActivePlan(planId);
                  toast.success(t("activated"));
                })
              }
            >
              <CheckCircle2 /> {t("setActive")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={() =>
              start(async () => {
                await duplicatePlan(planId, t("copySuffix"));
                toast.success(t("duplicated"));
              })
            }
          >
            <Copy /> {t("duplicate")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setSharing(true)}>
            <Share2 /> {t("share")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => (active ? toast.error(t("deleteActive")) : setConfirm(true))}
          >
            <Trash2 /> {t("deletePlan")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SharePlanDrawer planId={planId} planName={planName} open={sharing} onOpenChange={setSharing} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tc("deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{tc("deleteConfirmText")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc("cancel")}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" disabled={pending} onClick={() =>
                start(async () => {
                  await deletePlan(planId);
                  navigate("/plans", { replace: true });
                })
              }>
              {tc("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
